<template>
  <table class="table table-striped table-bordered table-condensed hypervisor-list-table">
    <thead>
      <tr>
        <th class="hypervisor-col hypervisor-col--name"><div>Name</div></th>
        <th class="hypervisor-col hypervisor-col--status"><div class="text-center">Status</div></th>
        <th class="hypervisor-col hypervisor-col--cpu hidden-tablet"><div class="text-center">CPU(MHz)[Core]</div></th>
        <th class="hypervisor-col hypervisor-col--memory"><div class="text-center">Memory Usage</div></th>
        <th class="hypervisor-col hypervisor-col--cpu-usage"><div class="text-center">CPU Usage</div></th>
        <th class="hypervisor-col hypervisor-col--ip hidden-tablet"><div class="text-center">IP address</div></th>
        <th class="hypervisor-col hypervisor-col--vms hypervisor-col--vms-responsive"><div class="text-center">VMs</div></th>
        <th v-if="showActions" class="hypervisor-col hypervisor-col--actions"><div class="text-center">Actions</div></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="`hv-${row.id}`">
        <td>
          <div class="hypervisor-name-cell">
            <div class="hypervisor-name-cell__name">{{ row.name }}</div>
            <div v-if="row.description" class="hypervisor-name-cell__description hidden-tablet">{{ row.description }}</div>
          </div>
        </td>
        <td class="text-center">
          <span class="app-status-chip" :class="row.status_class">{{ row.status_label }}</span>
          <button v-if="row.maintenance_progress_url" type="button" class="btn btn-link btn-xs hv-maintenance-view-progress" @click="openMaintenance(row)">View progress</button>
        </td>
        <td class="hidden-tablet hypervisor-cell--cpu">{{ row.cpu_label }}</td>
        <td>
          <div class="progress">
            <div class="progress-bar" :class="progressClass(row.mem_usage)" roll="progressbar" aria-valuemin="0" aria-valuemax="100" :style="{ width: `${row.mem_usage}%` }">
              <span style="display:none;">{{ row.mem_usage }}%</span>
            </div>
          </div>
        </td>
        <td>
          <div class="progress">
            <div class="progress-bar" :class="progressClass(row.cpu_usage)" roll="progress" aria-valuemin="0" aria-valuemax="100" :style="{ width: `${row.cpu_usage}%` }">
              <span style="display:none;">{{ row.cpu_usage }}%</span>
            </div>
          </div>
        </td>
        <td class="hidden-tablet">{{ row.ipaddress }}</td>
        <td class="hypervisor-cell--vms-responsive">{{ row.vm_count_label }}</td>
        <td v-if="showActions" class="hypervisor-actions-col">
          <div class="hypervisor-more-actions" v-if="row.actions">
            <button
              type="button"
              class="hypervisor-more-actions__gear"
              :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
              @click.stop="toggleMenu(row.id, $event)"
            >
              <AppIcon name="settings" />
              <AppIcon name="chevron-down" class="hypervisor-more-actions__caret" />
            </button>
            <div
              v-if="openMenuId === row.id"
              class="hypervisor-more-actions__menu"
              :style="menuStyle"
              @click.stop
            >
              <button v-if="row.actions.maintenance_start_url" type="button" class="hypervisor-more-actions__item" @click.stop="startMaintenance(row)">
                <AppIcon name="settings" /><span>Start Maintenance</span>
              </button>
              <button v-if="row.actions.maintenance_end_url" type="button" class="hypervisor-more-actions__item" @click.stop="endMaintenance(row)">
                <AppIcon name="play" /><span>End Maintenance</span>
              </button>
              <a :href="row.actions.edit_url" class="hypervisor-more-actions__item">
                <AppIcon name="edit" />
                <span>Edit</span>
              </a>
              <form
                :action="row.actions.destroy_url"
                method="post"
                class="hypervisor-more-actions__form"
                :data-confirm-message="row.actions.destroy_confirm_message"
                @click.stop
              >
                <input v-if="csrfToken" type="hidden" name="authenticity_token" :value="csrfToken">
                <input type="hidden" name="_method" value="delete">
                <button type="submit" class="hypervisor-more-actions__item hypervisor-more-actions__item--danger" title="Delete HyperVisor">
                  <AppIcon name="delete" />
                  <span>Delete</span>
                </button>
              </form>
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <div v-if="maintenance.open" class="hv-maintenance-backdrop">
    <section class="hv-maintenance-panel" role="dialog" aria-modal="true" aria-label="Hypervisor maintenance progress">
      <h4>Maintenance: {{ maintenance.hvName }}</h4>
      <p>{{ maintenance.message }}</p>
      <div class="progress"><div class="progress-bar progress-bar-info progress-bar-striped active" :style="{ width: `${maintenance.percent}%` }">{{ maintenance.percent }}%</div></div>
      <p class="text-muted">{{ maintenance.completedVms }} / {{ maintenance.totalVms }} VMs completed</p>
      <div class="hv-maintenance-vms">
        <div v-for="vm in maintenance.migrations" :key="vm.vm_id" class="hv-maintenance-vm">
          <div class="hv-maintenance-vm__header">
            <div>
              <strong class="hv-maintenance-vm__name">{{ vm.vm_name }}</strong>
              <small v-if="vm.destination" class="hv-maintenance-vm__destination">Destination: {{ vm.destination }}</small>
            </div>
            <span class="hv-maintenance-vm__percent" :class="migrationStatusClass(vm)">{{ migrationStatusLabel(vm) }}</span>
          </div>
          <div class="progress"><div class="progress-bar progress-bar-info" :style="{ width: `${vm.percent || 0}%` }"></div></div>
        </div>
      </div>
      <div v-if="maintenance.error" class="alert alert-danger">{{ maintenance.error }}</div>
      <div class="text-right hv-maintenance-actions">
        <button v-if="maintenance.running && maintenance.stopUrl" type="button" class="btn btn-danger" @click="stopMaintenance">Stop Maintenance</button>
        <button type="button" class="btn btn-default" @click="closeMaintenance">Close</button>
      </div>
    </section>
  </div>
</template>


<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [] })
  }
})

const rowsState = ref(props.initialData?.rows || [])
const rows = computed(() => rowsState.value)
const showActions = computed(() => rows.value.some((row) => row.actions))
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const openMenuId = ref(null)
const menuStyle = ref({})
const maintenance = ref({
  open: false, running: false, completed: false, hvName: "", percent: 0,
  message: "", totalVms: 0, completedVms: 0, migrations: [], error: "", stopUrl: ""
})
let maintenanceTimer = null


watch(
  () => props.initialData,
  (nextValue) => {
    rowsState.value = nextValue?.rows || []
  }
)

async function requestJson(url, method = "GET") {
  const headers = { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" }
  if (csrfToken && method !== "GET") headers["X-CSRF-Token"] = csrfToken
  const response = await fetch(url, { method, headers, credentials: "same-origin" })
  const text = await response.text()
  let payload = {}
  try { payload = text ? JSON.parse(text) : {} } catch { payload = {} }
  if (!response.ok) throw new Error(payload.message || text || "Request failed.")
  return payload
}

async function resumeMaintenance() {
  const row = rowsState.value.find((item) => item.maintenance_progress_url)
  if (row) await openMaintenance(row)
}

async function openMaintenance(row) {
  if (maintenanceTimer) window.clearInterval(maintenanceTimer)
  maintenance.value = { open: true, running: true, completed: false, hvName: row.name, percent: 0, message: "Reconnecting to maintenance progress...", totalVms: 0, completedVms: 0, migrations: [], error: "", stopUrl: row.maintenance_stop_url || "" }
  await pollMaintenance(row.maintenance_progress_url)
  if (maintenance.value.running) {
    maintenanceTimer = window.setInterval(() => pollMaintenance(row.maintenance_progress_url), 1000)
  }
}

async function startMaintenance(row) {
  if (!window.confirm(`Put ${row.name} into maintenance mode and live migrate its VMs?`)) return
  closeMenu()
  maintenance.value = { open: true, running: true, completed: false, hvName: row.name, percent: 0, message: "Starting maintenance mode...", totalVms: 0, completedVms: 0, migrations: [], error: "", stopUrl: "" }
  try {
    const payload = await requestJson(row.actions.maintenance_start_url, "POST")
    maintenance.value.stopUrl = payload.stop_url || ""
    await pollMaintenance(payload.status_url)
    if (maintenance.value.running) {
      maintenanceTimer = window.setInterval(() => pollMaintenance(payload.status_url), 1000)
    }
  } catch (error) {
    maintenance.value.running = false
    maintenance.value.error = error.message
  }
}

async function pollMaintenance(statusUrl) {
  try {
    const payload = await requestJson(statusUrl)
    maintenance.value.percent = Number(payload.percent || 0)
    maintenance.value.message = payload.message || "Migrating VMs..."
    maintenance.value.totalVms = Number(payload.total_vms || 0)
    maintenance.value.completedVms = Number(payload.completed_vms || 0)
    maintenance.value.migrations = Array.isArray(payload.migrations) ? payload.migrations : []
    if (["completed", "failed", "stopped"].includes(payload.status)) {
      window.clearInterval(maintenanceTimer)
      maintenanceTimer = null
      maintenance.value.running = false
      maintenance.value.completed = payload.status === "completed"
      maintenance.value.error = payload.status === "failed" ? (payload.error || payload.message) : ""
    }
  } catch (error) {
    window.clearInterval(maintenanceTimer)
    maintenanceTimer = null
    maintenance.value.running = false
    maintenance.value.error = error.message
  }
}

async function stopMaintenance() {
  if (!maintenance.value.stopUrl || !window.confirm("Stop maintenance migration after the current VM finishes?")) return
  try {
    const payload = await requestJson(maintenance.value.stopUrl, "POST")
    maintenance.value.message = payload.message || "Stop requested..."
  } catch (error) {
    maintenance.value.error = error.message
  }
}

async function endMaintenance(row) {
  if (!window.confirm(`End maintenance mode for ${row.name}?`)) return
  closeMenu()
  try {
    const payload = await requestJson(row.actions.maintenance_end_url, "POST")
    if (payload.row_data) {
      const index = rowsState.value.findIndex((item) => item.id === row.id)
      if (index >= 0) rowsState.value.splice(index, 1, payload.row_data)
    }
  } catch (error) {
    window.alert(error.message)
  }
}

function closeMaintenance() {
  maintenance.value.open = false
  if (maintenance.value.completed) window.location.reload()
}

function progressClass(value) {
  if (value <= 60) return 'progress-bar-info'
  if (value <= 80) return 'progress-bar-warning'
  return 'progress-bar-danger'
}

function migrationStatusLabel(vm) {
  if (vm.status === "completed") return "100% Completed"
  if (vm.status === "skipped") return "Skipped"
  if (vm.status === "failed") return "Failed"
  if (!vm.status || vm.status === "queued") return "Waiting for migration..."
  return `${Number(vm.percent || 0)}%`
}

function migrationStatusClass(vm) {
  return `hv-maintenance-vm__percent--${vm.status || "queued"}`
}

function handleHyperVisorUpdate(event) {
  const payload = event.detail || {}

  if (payload.type === 'removed') {
    rowsState.value = rowsState.value.filter((row) => row.id !== payload.hyper_visor_id)
    return
  }

  if (payload.type !== 'changed' || !payload.row_data) {
    return
  }

  const nextRow = payload.row_data
  const index = rowsState.value.findIndex((row) => row.id === nextRow.id)
  if (index === -1) return

  const nextRows = rowsState.value.slice()
  nextRows.splice(index, 1, { ...rowsState.value[index], ...nextRow })
  rowsState.value = nextRows
}

function toggleMenu(rowId, event) {
  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 180
  const menuHeight = 190
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = rowId
  menuStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px` }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

function handleDocumentClick(event) {
  if (!event.target.closest('.hypervisor-more-actions__menu') && !event.target.closest('.hypervisor-more-actions__gear')) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('scc:hyper-visor-state-changed', handleHyperVisorUpdate)
  document.addEventListener('click', handleDocumentClick)
  resumeMaintenance()
})

onBeforeUnmount(() => {
  if (maintenanceTimer) window.clearInterval(maintenanceTimer)
  document.removeEventListener('scc:hyper-visor-state-changed', handleHyperVisorUpdate)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
.hv-maintenance-backdrop { position: fixed; inset: 0; z-index: 2050; display: flex; align-items: center; justify-content: center; padding: 24px; background: rgba(15, 23, 42, 0.58); }
.hv-maintenance-panel { width: min(720px, 96vw); max-height: 86vh; overflow: auto; padding: 24px; border-radius: 14px; background: #fff; box-shadow: 0 24px 70px rgba(15, 23, 42, 0.32); }
.hv-maintenance-view-progress { display: block; margin: 4px auto 0; padding: 0; font-size: 11px; }
.hv-maintenance-actions { display: flex; justify-content: flex-end; gap: 8px; }
.hv-maintenance-vms { margin: 16px 0; }
.hv-maintenance-vm { padding: 10px 0; border-top: 1px solid #e5e7eb; }
.hv-maintenance-vm .progress { height: 8px; margin: 6px 0; }
.hv-maintenance-vm__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.hv-maintenance-vm__name { display: block; font-size: 14px; }
.hv-maintenance-vm__destination { display: block; margin-top: 2px; color: #64748b; }
.hv-maintenance-vm__percent { min-width: 58px; padding: 4px 9px; border-radius: 999px; background: #e8f1fb; color: #24567f; font-size: 12px; font-weight: 700; text-align: center; white-space: nowrap; font-variant-numeric: tabular-nums; }
.hv-maintenance-vm__percent--queued { background: #f1f5f9; color: #64748b; }
.hv-maintenance-vm__percent--completed { background: #e8f7ee; color: #247044; }
.hv-maintenance-vm__percent--failed { background: #fdecec; color: #a32727; }


.hypervisor-list-table {
  table-layout: fixed;
}

.hypervisor-col--status {
  width: 146px;
}

.hypervisor-list-table :deep(.app-status-chip) {
  min-width: 126px;
  padding-inline: 14px;
}

.hypervisor-col--cpu {
  width: 172px;
}

.hypervisor-col--memory,
.hypervisor-col--cpu-usage {
  width: 118px;
}

.hypervisor-col--ip {
  width: 146px;
}

.hypervisor-col--vms {
  width: 156px;
}

.hypervisor-name-cell {
  text-align: left;
}

.hypervisor-name-cell__name {
  font-weight: 600;
  color: #0f172a;
}

.hypervisor-name-cell__description {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

.hypervisor-col--actions {
  width: 74px;
}

.hypervisor-more-actions {
  position: relative;
}

.hypervisor-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.hypervisor-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.hypervisor-more-actions__menu {
  z-index: 60;
  min-width: 180px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.hypervisor-more-actions__form {
  display: block;
}

.hypervisor-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.hypervisor-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.hypervisor-more-actions__item--danger {
  color: #9f1d2d;
}

.hypervisor-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

@media (max-width: 1350px) {
  .hypervisor-col--cpu,
  .hypervisor-cell--cpu {
    display: none;
  }
}

@media (max-width: 1150px) {
  .hypervisor-col--vms-responsive,
  .hypervisor-cell--vms-responsive {
    display: none;
  }
}
</style>
